'use client';

import { getProduct, products } from '@/lib/products';
import { BrowserShot, Tilt } from './shots';
import { Reveal } from './Reveal';
import { ProductHero } from './hero';
import {
  AccentGlow,
  DemoBand,
  FeatureRows,
  FloatChip,
  NextProducts,
  ProductMarquee,
  SectionHead,
  StatGrid,
} from './blocks';

const product = getProduct('dilacrm')!;
const A = product.accent;
const IMG = '/images/products/dilacrm';

export function DilaCrmPage() {
  return (
    <div className="self-styled relative text-white">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <ProductHero
        name="DilaCRM"
        accent={A}
        title="every message is a deal"
        lede="One inbox for WhatsApp, Instagram, Messenger, Telegram, TikTok and email, with AI that drafts in your customer's language."
        secondary={{ label: 'See the inbox', href: '#inbox' }}
        liveHref={product.liveUrl}
        liveLabel={`${product.liveLabel} · ${product.domain}`}
      >
        <AccentGlow accent={A} className="left-1/4 top-1/4 h-80 w-80" />
        <div className="relative rotate-1">
          <Tilt>
            <BrowserShot
              src={`${IMG}/hero-approval.webp`}
              alt={product.imageAlt}
              url={product.domain}
              accent={A}
              priority
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Tilt>
        </div>
        <FloatChip accent={A} className="-left-4 top-10" dot>
          AI draft · awaiting approval
        </FloatChip>
        <FloatChip accent={A} className="-right-3 top-1/3" alt>
          WhatsApp · Instagram
        </FloatChip>
        <FloatChip accent={A} className="bottom-8 left-1/4">
          Telegram · TikTok · Email
        </FloatChip>
      </ProductHero>

      {/* ── The one marquee: channels + languages ────────────────────── */}
      <ProductMarquee
        accent={A}
        items={[
          'WhatsApp',
          'Instagram DMs',
          'Messenger',
          'Telegram',
          'TikTok',
          'Email',
          'Voice AI',
          'کوردی',
          'العربية',
          'English',
        ]}
      />

      {/* ── The inbox (split: copy left, shot right) ─────────────────── */}
      <section id="inbox" aria-label="Unified inbox" className="px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <SectionHead
              accent={A}
              title="one inbox. every channel."
              lede="Connect the WhatsApp you already use, your Instagram, your page, your Telegram bot and your mailbox. Everything lands in one queue with lanes, snooze and presence."
            />
            <FeatureRows
              accent={A}
              className="mt-10"
              rows={[
                {
                  title: 'Context on every thread',
                  body: 'Contact details, tags, deals and lead temperature sit beside the conversation, not in another tab.',
                },
                {
                  title: 'The clock is visible',
                  body: "Each channel's messaging window is tracked, with an approved-template escape when the window closes.",
                },
              ]}
            />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-right-8 bottom-0 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/inbox.webp`}
              alt="DilaCRM conversation view with a Messenger thread, CRM side panel, lead card and reply-window countdown."
              url={`${product.domain}/inbox`}
              accent={A}
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Human in the loop (centered stack) ───────────────────────── */}
      <section aria-label="AI approval" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              kicker="Human in the loop"
              accent={A}
              title="your ai drafts. you approve."
              lede="Three autonomy modes, armed per channel. High-confidence replies can go out on their own; everything else queues as a draft with the reason it paused. Nothing goes out unsupervised."
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120}>
            <FeatureRows
              accent={A}
              className="mt-14"
              rows={[
                {
                  title: 'Drafts only',
                  body: 'AI writes, humans send. Every reply waits for an approve, an edit, or a reject with a reason it learns from.',
                },
                {
                  title: 'Replies when confident',
                  body: 'Above your confidence threshold it sends and schedules the follow-up. Below it, the draft lands in your queue.',
                },
                {
                  title: 'Fully autonomous',
                  body: 'Arm it per channel with abuse protection watching for flooding, manipulation and free-chatbot tourists.',
                },
              ]}
            />
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
              Grounded in your knowledge base · playbooks for real estate, solar, hospitality and grocery
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Comments (split: shot left, copy right) ──────────────────── */}
      <section aria-label="Comment automation" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="relative order-2 lg:order-1 lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-left-8 top-10 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/comments-ku.webp`}
              alt="DilaCRM comments manager showing an Instagram comment in Sorani Kurdish with an AI-drafted reply pending approval."
              url={`${product.domain}/comments`}
              accent={A}
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
          <Reveal className="order-1 lg:order-2 lg:col-span-5">
            <SectionHead
              accent={A}
              title="comments become customers"
              lede="Facebook, Instagram and TikTok comments in one queue. AI drafts the public reply and the private DM, hides the toxic spam, and answers in Sorani when the comment is in Sorani."
            />
            <FeatureRows
              accent={A}
              className="mt-10"
              rows={[
                {
                  title: 'Comment to DM, on autopilot',
                  body: '“Comment PRICE and we will message you.” Keyword rules turn ad comments into DM conversations, ManyChat-style, without another subscription.',
                },
                {
                  title: 'Moderation that never sleeps',
                  body: 'Abuse is classified and auto-hidden with per-day caps, per-channel opt-in and a full audit trail.',
                },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Campaigns + ads (asymmetric bento) ───────────────────────── */}
      <section aria-label="Campaigns and ads" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] gap-6 lg:grid-cols-12">
          <Reveal className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9 lg:col-span-5">
            <h3 className="font-six-caps text-5xl lowercase leading-[0.9] text-white md:text-6xl">
              campaigns that land
            </h3>
            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-white/55 md:text-base">
              Approved WhatsApp templates to everyone, one language, your VIP
              batch, or repeat-loyal customers. Every recipient tracked from
              sent to read, every failed message refunded to your wallet.
            </p>
            <div className="mt-8 flex-1">
              <BrowserShot
                src={`${IMG}/campaigns.webp`}
                alt="DilaCRM WhatsApp campaign composer with credit balance, template picker, audience options and estimated cost."
                url={`${product.domain}/campaigns`}
                accent={A}
                aspect="aspect-[4/3]"
                sizes="(max-width: 1024px) 100vw, 38vw"
              />
            </div>
          </Reveal>
          <Reveal
            className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9 lg:col-span-7"
            delay={120}
          >
            <h3 className="font-six-caps text-5xl lowercase leading-[0.9] text-white md:text-6xl">
              ad spend, finally honest
            </h3>
            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-white/55 md:text-base">
              Meta and TikTok accounts side by side: spend, reach, CTR, CPC,
              conversions and ROAS. Pause a campaign or fix a budget without
              leaving the screen.
            </p>
            <div className="mt-8 flex-1">
              <BrowserShot
                src={`${IMG}/ads.webp`}
                alt="DilaCRM ads performance dashboard for TikTok showing spend, impressions, clicks, conversions and ROAS with a spend-over-time chart."
                url={`${product.domain}/social-ads`}
                accent={A}
                aspect="aspect-[16/9]"
                sizes="(max-width: 1024px) 100vw, 55vw"
              />
            </div>
            <ul className="mt-7 flex flex-wrap gap-2.5" aria-label="Tracked ad metrics">
              {['Spend', 'Reach', 'CTR', 'CPC', 'CPM', 'Conversions', 'ROAS'].map((metric) => (
                <li
                  key={metric}
                  className="rounded-full border border-white/12 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60"
                >
                  {metric}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── CRM (full-width band) ────────────────────────────────────── */}
      <section aria-label="CRM pipeline" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title="a crm that fills itself"
              lede="Contacts are born from conversations, deals carry dual IQD and USD values, and the pipeline tells you which lead is going cold before it does."
            />
          </Reveal>
          <Reveal delay={120} className="relative mt-14">
            <AccentGlow accent={A} className="right-1/4 -top-8 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/pipeline.webp`}
              alt="DilaCRM Kanban pipeline with new inquiry, qualified, site visit and quoted stages holding IQD and USD deal values."
              url={`${product.domain}/crm`}
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
          <Reveal delay={180}>
            <FeatureRows
              accent={A}
              className="mt-14"
              rows={[
                {
                  title: 'Rotting alerts per stage',
                  body: 'Each stage gets its own “gone quiet after N days” threshold, so follow-ups are triggered before the lead forgets you.',
                },
                {
                  title: 'Hot, warm, cold at a glance',
                  body: 'Lead temperature, last AI signal and next-best-action sit on the card, and round-robin routing assigns new leads fairly.',
                },
                {
                  title: 'Imports without the spreadsheet fight',
                  body: 'CSV and Excel uploads up to 5,000 rows with AI column mapping, plus duplicate merge that keeps every conversation.',
                },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <section aria-label="DilaCRM in numbers" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-28 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <StatGrid
              accent={A}
              stats={[
                { value: '06', label: 'Messaging channels' },
                { value: '05', label: 'Interface languages' },
                { value: '03', label: 'AI reply languages' },
                { value: 'Human', label: 'approval controls' },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Language band (duo shots) ────────────────────────────────── */}
      <section aria-label="Languages" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title="an inbox that speaks sorani"
              lede="A full right-to-left interface, and an AI that answers in Kurdish Sorani, Arabic or English without being asked twice. Your customers write how they write; it meets them there."
              className="max-w-4xl"
            />
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7" delay={100}>
              <BrowserShot
                src={`${IMG}/rtl-ar.webp`}
                alt="DilaCRM inbox fully mirrored in Arabic, showing the right-to-left interface, wallet card and setup checklist."
                url={product.domain}
                accent={A}
                aspect="aspect-[16/10]"
                sizes="(max-width: 1024px) 100vw, 55vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                Full RTL, Arabic and Kurdish
              </p>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:pt-16" delay={200}>
              <BrowserShot
                src={`${IMG}/channels.webp`}
                alt="DilaCRM channel connection page for Meta, WhatsApp and TikTok with per-service checkboxes and QR WhatsApp pairing."
                url={`${product.domain}/channels`}
                accent={A}
                aspect="aspect-[4/3]"
                sizes="(max-width: 1024px) 100vw, 38vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                Connect a channel in five minutes
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Demo + cross-sell ────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="DilaCRM"
            title="stop losing the 11:47 pm lead"
            sub="A lead messaged at 11:47 PM. DilaCRM answered before they could leave. Put that on every channel you own."
            primary={{ label: 'Book a demo', href: '/contact' }}
            secondary={{ label: 'Open the live inbox', href: product.liveUrl, external: true }}
            accent={A}
            note={`Live: ${product.domain}`}
          />
          <NextProducts
            className="mb-24"
            items={products
              .filter((p) => p.slug !== 'dilacrm')
              .map((p) => ({ slug: p.slug, name: p.name, tagline: p.tagline, accent: p.accent }))}
          />
        </div>
      </div>
    </div>
  );
}
